import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Save, Upload, Image as ImageIcon } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, apiUrl } from "@/lib/api";
import { COUNTRIES, INDUSTRIES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SponsorProfile() {
  const { t } = useTranslation();
  const [p, setP] = useState({});
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/api/sponsor/profile");
      setP(data);
    })();
  }, []);
  const set = (patch) => setP((v) => ({ ...v, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/api/sponsor/profile", p);
      setP(data);
      toast.success(t("sponsor_profile.toast_saved"));
    } catch (e) { toast.error(t("sponsor_profile.toast_save_error")); } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    set({ logo_url: apiUrl(data.url) });
  };

  return (
    <DashboardLayout
      title={t("sponsor_profile.page_title")}
      subtitle={t("sponsor_profile.page_subtitle")}
      actions={
        <Button onClick={save} disabled={saving} data-testid="button-save-sponsor-profile" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
          <Save size={16} className="mr-2" /> {saving ? t("sponsor_profile.saving") : t("sponsor_profile.save")}
        </Button>
      }
    >
      <div className="kpi-card p-6">
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
            {p.logo_url ? <img src={p.logo_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="text-slate-400" />}
          </div>
          <div>
            <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} />
            <Button variant="outline" onClick={() => logoRef.current?.click()}><Upload size={14} className="mr-2" /> {t("sponsor_profile.upload_logo")}</Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          <Field label={t("sponsor_profile.field_commercial_name")}><Input value={p.commercial_name || ""} onChange={(e) => set({ commercial_name: e.target.value })} data-testid="input-sponsor-name" /></Field>
          <Field label={t("sponsor_profile.field_legal_name")}><Input value={p.legal_name || ""} onChange={(e) => set({ legal_name: e.target.value })} /></Field>
          <Field label={t("sponsor_profile.field_organization_type")}>
            <Select value={p.organization_type || "empresa"} onValueChange={(v) => set({ organization_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="empresa">{t("sponsor_profile.org_empresa")}</SelectItem>
                <SelectItem value="fundacion">{t("sponsor_profile.org_fundacion")}</SelectItem>
                <SelectItem value="ong">{t("sponsor_profile.org_ong")}</SelectItem>
                <SelectItem value="institucion">{t("sponsor_profile.org_institucion")}</SelectItem>
                <SelectItem value="patrocinador">{t("sponsor_profile.org_patrocinador")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("sponsor_profile.field_industry")}>
            <Select value={p.industry || ""} onValueChange={(v) => set({ industry: v })}>
              <SelectTrigger><SelectValue placeholder={t("sponsor_profile.field_industry_placeholder")} /></SelectTrigger>
              <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("sponsor_profile.field_country")}>
            <Select value={p.country || ""} onValueChange={(v) => set({ country: v })}>
              <SelectTrigger><SelectValue placeholder={t("sponsor_profile.field_country_placeholder")} /></SelectTrigger>
              <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("sponsor_profile.field_city")}><Input value={p.city || ""} onChange={(e) => set({ city: e.target.value })} /></Field>
          <Field label={t("sponsor_profile.field_website")}><Input value={p.website || ""} onChange={(e) => set({ website: e.target.value })} /></Field>
          <Field label={t("sponsor_profile.field_tax_id")}><Input value={p.tax_id || ""} onChange={(e) => set({ tax_id: e.target.value })} /></Field>
        </div>

        <Field label={t("sponsor_profile.field_description")} className="mt-5">
          <Textarea rows={4} value={p.description || ""} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <Field label={t("sponsor_profile.field_purpose")} className="mt-5">
          <Textarea rows={3} value={p.purpose || ""} onChange={(e) => set({ purpose: e.target.value })} />
        </Field>

        <h3 className="section-title mt-10">{t("sponsor_profile.contact_person_title")}</h3>
        <div className="grid md:grid-cols-2 gap-5 mt-4">
          <Field label={t("sponsor_profile.field_contact_name")}><Input value={p.contact_name || ""} onChange={(e) => set({ contact_name: e.target.value })} /></Field>
          <Field label={t("sponsor_profile.field_contact_role")}><Input value={p.contact_role || ""} onChange={(e) => set({ contact_role: e.target.value })} /></Field>
          <Field label={t("sponsor_profile.field_contact_email")}><Input value={p.contact_email || ""} onChange={(e) => set({ contact_email: e.target.value })} /></Field>
          <Field label={t("sponsor_profile.field_contact_phone")}><Input value={p.contact_phone || ""} onChange={(e) => set({ contact_phone: e.target.value })} /></Field>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="text-sm text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
